import React, {useEffect, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {toAbsoluteUrl} from '../../../helpers'
import {KTCard, KTCardBody, Select, InputPrice, Loading, Datepicker} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {salaryUpdate, salaryGetId} from '../../../redux/slices/staff.slice'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  loading: state.lookup.loading,
})

const selectStaffData = (state) => ({
  isRedirect: state.staff.isRedirect,
  detail: state.staff.detail,
  loading: state.staff.loading,
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  const {projectLookupList, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectStaffData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(salaryGetId(id))
    dispatch(projectLookup())
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    ProgressPaymentDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    DueDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    Payment: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: '',
    ProgressPaymentDate: '',
    Payment: '',
    DueDate: '',
    Name: '',
    ProjectId: null,
  })

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      dispatch(salaryUpdate(values))
    },
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && !isFormPopulatedRef.current && formikRef.current) {
      const populatedValues = {
        Id: detail.id || '',
        ProgressPaymentDate: detail.progressPaymentDate ? new Date(detail.progressPaymentDate) : new Date(),
        Payment: detail.payment || '',
        DueDate: detail.dueDate ? new Date(detail.dueDate) : new Date(),
        Name: detail.name || '',
        ProjectId: detail.projectId || null,
      }
      
      formikRef.current.setValues(populatedValues)
      isFormPopulatedRef.current = true
    }
  }, [detail])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.SALARY'}),
      path: '/staff/salary',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/staff/salary/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.SALARY.EDIT'})}</PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-7'>
                <div className='d-flex align-items-center mb-7'>
                  <div className='symbol symbol-50px me-5'>
                    <img
                      src={toAbsoluteUrl('/images/icons/duotune/communication/com013.svg')}
                      alt=''
                    />
                  </div>
                  <div className='flex-grow-1'>
                    <div className='text-dark fw-bold fs-6'>{detail.staffNameSurname}</div>
                  </div>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'EXPLANATION'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <textarea
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    rows={4}
                    {...formik.getFieldProps('Name')}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENT.PROJECT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PROGRESS_PAYMENT_DATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    required
                    {...formik.getFieldProps('ProgressPaymentDate')}
                    onChange={([date]) => formik.setFieldValue('ProgressPaymentDate', date)}
                  />
                  {formik.touched.ProgressPaymentDate && formik.errors.ProgressPaymentDate && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProgressPaymentDate}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PAYMENT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('Payment')}
                    onChange={(value) => formik.setFieldValue('Payment', Number(value))}
                  />
                  {formik.touched.Payment && formik.errors.Payment && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Payment}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PAYMENT.DUE.DATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    required
                    {...formik.getFieldProps('DueDate')}
                    onChange={([date]) => formik.setFieldValue('DueDate', date)}
                  />

                  {formik.touched.DueDate && formik.errors.DueDate && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.DueDate}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
